import * as React from 'react';
import './App.css';

interface IWorksheetState {
  combination: Array<Array<number>>,
  bottomRange: number,
  topRange: number,
  limit: number,
  showAnswers: boolean,
  isVertical: boolean,
  operator: string,
  largeNumberOnTop: boolean,
  withoutRemainders: boolean
}

const operatorMap = {
  "+": "+",
  "-": "-",
  "×": "*",
  "÷": "/"
}

const calc = (a, b, operator) => {
  if (operator === "+") {
    return a + b
  } else if (operator === "-") {
    return a - b
  } else if (operator === "*") {
    return a * b
  } else if (operator === "/") {
    return a / b
  }
}

function Problem (props) {

  const answer = props.operator === '÷' && !props.withoutRemainders ? calc(props.a, props.b, operatorMap[props.operator]).toFixed(1) : calc(props.a, props.b, operatorMap[props.operator])

  return (
    <div className='Problem'>
      <div className='Problem-FirstNumber'>{props.a}</div>
      <div className='Problem-Sign'>{props.operator}</div>
      <div className='Problem-SecondNumber'>{props.b}</div>
      <div className='Problem-Answer'>{props.showAnswers ? answer : null}</div>
    </div>
  )
}

function Twitter () {
  return (
    <div className="Twitter small noPrint">Created by <a href="https://michaelsoolee.com/about/" target="_blank" rel="noreferrer">Michael Lee</a></div>
  )
}

function factorial(num: number) {
  if (num < 0) 
        return -1;
  else if (num === 0) 
      return 1;
  else {
      return (num * factorial(num - 1));
  }
}

export class Worksheet extends React.Component<IWorksheetState>{
  constructor(props) {
    super(props)
    this.state = {
      combinations: [],
      bottomRange: 1,
      topRange: 9,
      limit: 42,
      showAnswers: false,
      isVertical: true,
      operator: "+",
      largerNumberOnTop: false,
      withoutRemainders: true
    }
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.toggleAnswers = this.toggleAnswers.bind(this)
    this.print = this.print.bind(this)
  }

  handleSubmit(event) {
    this.fillProblems(this.state.limit)
    event.preventDefault()
  }
  
  handleChange(event) {
    const key = event.target.name
    const { checked } = event.target

    this.setState({ [key]: event.target.value === "on" ? checked : event.target.value}, () => {
      this.fillProblems(this.state.limit)
    })
  }

  toggleAnswers() {
    this.setState({
      showAnswers: !this.state.showAnswers
    })
  }

  print() {
    window.print()
  }

  componentDidMount() {
    this.fillProblems(this.state.limit)
  }

  atLimit() {
    let top = this.state.topRange
    let bottom = this.state.bottomRange
    if (this.state.largerNumberOnTop) {
      let choices = top - bottom + 1
      let numerator = factorial((choices + 2) - 1)
      let denominator = factorial(choices - 1) * factorial(2)
      let largeLimit = numerator/denominator

      if (largeLimit >= this.state.limit) {
        return false
      }
    
      return true
    } else {
      let limit = top - bottom + 1
      
      if ((limit * limit) >= this.state.limit) {
        return false
      }
      
      return true
    }
    
  }

  inCombinations(toCheck, problems) {
    let existsInProblems = false
    let combinationsToCheck = problems

    combinationsToCheck.every((problem) => {
      if (problem[0] === toCheck[0] && problem[1] === toCheck[1]) {
        existsInProblems = true
        return false
      } 
      return true
    })
    return existsInProblems
  }

  generateRandomNumbers() {
    let numbers = []
    
    let min = Math.ceil(this.state.bottomRange)
    let max = Math.floor(this.state.topRange)
    
    for(let i = 1; i <= 2; i++) {
      numbers.push(Math.floor(Math.random() * (max - min + 1) + min))
    }

    if (this.state.withoutRemainders && this.state.operator === "÷") {
      const biggerNumber = numbers[0] * numbers[1]
      numbers.pop()
      numbers.unshift(biggerNumber)
    }
    
    if(this.state.largerNumberOnTop && numbers[1] > numbers[0]) {
      const biggerNumber = numbers.pop()
      numbers.unshift(biggerNumber)
    }
    
    return numbers
  }

  fillProblems(limit) {
    if(this.atLimit()) {
      alert(`I'm sorry, the numbers are too close, please change the smallest and largest numbers and try generating again.`)
      return
    }
    const problems = []

    for(let i = 0; i < limit; i++) {
      let problemNumbers = this.generateRandomNumbers()
      if (this.inCombinations(problemNumbers, problems)) {
        let problemIsUnique = false
        let uniqueNum = []
        while (problemIsUnique === false) {
          uniqueNum = this.generateRandomNumbers()
          if (this.inCombinations(uniqueNum, problems)) {
            continue
          } else {
            problemIsUnique = true
            break
          }
        }
        problems.push(uniqueNum)
      } else {
        problems.push(problemNumbers)
      }
    }

    this.setState({
      combinations: problems
    })
  }

  renderProblems() {
    const problems = []
    for(let i = 0; i < this.state.combinations.length; i++) {
      problems.push(
        <Problem 
          a={this.state.combinations[i][0]} 
          b={this.state.combinations[i][1]} 
          operator={this.state.operator} 
          key={i} 
          showAnswers={this.state.showAnswers}
          withoutRemainders={this.state.withoutRemainders} 
        />
      )
    }
    return problems
  }

  render() {
    return (
      <div className="Container">
        <h1 className="noPrint">Math worksheets generator</h1>
        <div className="Controls noPrint">
          <form onSubmit={this.handleSubmit}>
           <div className="flex gap-col3">
            <div className="grow1">
              <label className="flex flex-col">
              <div className="small">Smallest number</div>
              <input type="text" name="bottomRange" onChange={this.handleChange} value={this.state.bottomRange} className="Input grow1" />
              </label> 
            </div>
            <div className="grow1">
              <label className="flex flex-col">
              <div className="small">Largest number</div>
              <input type="text" name="topRange" onChange={this.handleChange} value={this.state.topRange} className="Input grow1" />
              </label>
            </div>
           </div>
            <div className="flex gap-col3 mt2">
              <div className="flex">
                <div>Which operator would you like to use?</div>
                <select name="operator" onChange={this.handleChange} className="ml1">
                  <option value="+">+ (Addition)</option>
                  <option value="-">- (Subtraction)</option>
                  <option value="×">× (Multiplication)</option>
                  <option value="÷">÷ (Division)</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label>Put large number on top? <input type="checkbox" onChange={this.handleChange} name="largerNumberOnTop" /></label>
                {this.state.operator === "÷" ? (
                  <label>Without remainders? <input type="checkbox" onChange={this.handleChange} name="withoutRemainders" checked={this.state.withoutRemainders}  /></label>
                ) : null}
              </div>
            </div>
            <div className="mt2 flex space-between">
              <input type="submit" value="Generate problems" />  
              <div>
                <input type="button" value={this.state.showAnswers ? "Hide answers" : "Show answers"} onClick={this.toggleAnswers} />
                <input type="button" value="Print sheet" onClick={this.print} className='ml2' />
              </div>
            </div>
          </form>
        </div>
        <div className={`Problems mt3 ${this.state.isVertical ? 'isVertical' : 'isHorizontal'}`}>
          {this.renderProblems()}
        </div>
        <Twitter />
      </div>
    )
  }
}

function App() {
  return (
    <Worksheet />
  );
}

export default App;
